import { Button, Card, Pagination, PermissionGate, Table, TableColumn } from '@/components/common';
import { usePermissions } from '@/hooks';
import { LabDTO, PaginationMetadata, InstitutionDTO } from '@/models';
import React from 'react';

interface LabTableProps {
  labs: LabDTO[];
  institutions: InstitutionDTO[];
  pagination: PaginationMetadata | null;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (lab: LabDTO) => void;
  onDelete: (lab: LabDTO) => void;
}

export const LabTable: React.FC<LabTableProps> = ({
  labs,
  institutions,
  pagination,
  currentPage,
  pageSize,
  pageSizeOptions,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
}) => {
  const { canPerform } = usePermissions();

  const getInstitutionName = (institutionId: string) => {
    const institution = institutions.find((i) => i.id === institutionId);
    return institution?.name || '-';
  };

  const columns: TableColumn<LabDTO>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
    },
    {
      key: 'labId',
      header: 'Lab ID',
      sortable: true,
      width: '100px',
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (lab) => lab.name || '-',
    },
    {
      key: 'institution',
      header: 'Institution',
      render: (lab) => getInstitutionName(lab.institutionId),
    },
    {
      key: 'amountDevices',
      header: 'Devices',
      render: (lab) => <span className="badge bg-info">{lab?.amountDevices ?? 0}</span>,
    },
    ...(canPerform('lab:edit') || canPerform('lab:delete')
      ? [
          {
            key: 'actions' as const,
            header: 'Actions',
            width: '150px',
            render: (lab: LabDTO) => (
              <div className="d-flex gap-2">
                <PermissionGate action="lab:edit">
                  <Button
                    variant="primary"
                    size="sm"
                    icon="fas fa-edit"
                    onClick={() => onEdit(lab)}
                  >
                    Edit
                  </Button>
                </PermissionGate>
                <PermissionGate action="lab:delete">
                  <Button
                    variant="danger"
                    size="sm"
                    icon="fas fa-trash"
                    onClick={() => onDelete(lab)}
                  />
                </PermissionGate>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card className="border-0 shadow-sm">
      <Table
        columns={columns}
        data={labs}
        keyExtractor={(lab) => lab.id}
        isLoading={isLoading}
        emptyMessage="No labs found. Click 'Add Lab' to create one."
      />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Rows per page:</small>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {pagination && (
            <small className="text-muted">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, pagination.totalCount)} of {pagination.totalCount}{' '}
              labs
            </small>
          )}
        </div>
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      </div>
    </Card>
  );
};
